import { COLORS } from '../../constants/theme';
import { formatNumber, formatEnumLabel } from '../../utils/format';
import { ProtectedBadge } from '../ProtectedBadge';
import { isProtected } from '../../utils/protection';
import type { ProvinceSnapshotDto } from '../../api/intel';

const MONO = "'JetBrains Mono', 'Fira Code', monospace";

interface MobileTargetResultCardProps {
  row: ProvinceSnapshotDto;
  /** Player's own networth, for the relative-NW% comparison — `null` before the first search
   *  response has returned `results.player` (the same brief window `TargetFinderTab.tsx` has). */
  playerNetworth: number | null;
  currentTick: number;
  onSelect: (row: ProvinceSnapshotDto) => void;
}

/**
 * SF-171 Component 7: one card per search result. Real `<button>` (not a bare `div`+`onClick`) so
 * it carries native focusability/keyboard-activation and a composed accessible name. Kingdom name
 * is shown (a targeting-relevant identity signal), but not the four numeric kingdom columns —
 * those move to the target-detail header (`MobileTargetDetailView`) rather than being dropped
 * entirely, per Key Implementation Note 6.
 */
export function MobileTargetResultCard({ row, playerNetworth, currentTick, onSelect }: MobileTargetResultCardProps) {
  const protectedTarget = isProtected(row.protectionExpiresAtTick, currentTick);
  const nwPct = playerNetworth ? Math.round((row.networth / playerNetworth) * 100) : null;
  const accessibleName = `${row.provinceName} (${row.location})${protectedTarget ? ', protected' : ''}`;

  return (
    <button
      type="button"
      data-target-id={row.provinceId}
      onClick={() => onSelect(row)}
      aria-label={accessibleName}
      style={{
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '10px 12px',
        minHeight: 44,
        background: COLORS.card,
        border: `1px solid ${COLORS.border}`,
        borderRadius: 8,
        cursor: 'pointer',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontWeight: 600, fontSize: 13, color: COLORS.text.primary }}>
          {row.provinceName}{' '}
          <span style={{ color: COLORS.link, fontSize: 11, fontFamily: MONO, fontWeight: 400 }}>({row.location})</span>
        </span>
        {protectedTarget && <ProtectedBadge protectionExpiresAtTick={row.protectionExpiresAtTick} currentTick={currentTick} />}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, fontSize: 11, color: COLORS.text.secondary }}>
        <span>{formatEnumLabel(row.race)}</span>
        <span style={{ color: COLORS.text.muted }}>·</span>
        <span>{formatEnumLabel(row.rank)}</span>
        <span style={{ color: COLORS.text.muted }}>·</span>
        <span style={{ color: COLORS.text.tertiary }}>{row.kingdomName}</span>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 }}>
        <span style={{ fontFamily: MONO, fontSize: 11, color: COLORS.text.secondary }}>
          Land (acres) {formatNumber(row.land)}
        </span>
        <span style={{ fontFamily: MONO, fontSize: 11, color: COLORS.gold }}>
          NW (gc) {formatNumber(row.networth)}
        </span>
        {nwPct !== null && (
          <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 600, color: nwPctColor(nwPct) }}>
            {nwPct}%
          </span>
        )}
      </div>
    </button>
  );
}

function nwPctColor(pct: number): string {
  if (pct < 70) return COLORS.positive;
  if (pct < 90) return '#82c8a0';
  if (pct <= 110) return COLORS.text.secondary;
  if (pct <= 130) return '#d4975f';
  return '#d48060';
}
